import { IBlock, IBlockData } from '@core/typings';
import { BasicType } from '@core/constants';
import React, { CSSProperties } from 'react';
import { createBlock } from '@core/utils/createBlock';
import { merge } from 'lodash';
import { BasicBlock } from '@core/components/BasicBlock';
import { t, resolvePreviewColor } from '@core/utils';

export type IWrapper = IBlockData<
	{
		'background-color'?: string;
		border?: string;
		'border-width'?: string;
		'border-style'?: string;
		'border-color'?: string;
		'border-top-width'?: string;
		'border-right-width'?: string;
		'border-bottom-width'?: string;
		'border-left-width'?: string;
		'border-radius'?: string;
		'full-width'?: string;
		direction?: 'ltr' | 'rtl';
		padding?: string;
		'text-align'?: CSSProperties['textAlign'];
		'background-repeat'?: string;
		'background-size'?: string;
	},
	Record<string, any>
>;

export const WRAPPER_DEFAULT_ATTRIBUTES: IWrapper['attributes'] = {
	padding: '20px 20px 20px 20px',
	border: 'none',
	direction: 'ltr',
	'text-align': 'center',
	'background-repeat': 'no-repeat',
	'background-size': 'cover',
	'border-width': '0px',
	'border-style': 'solid',
	'border-color': '#000000',
	'border-top-width': '',
	'border-right-width': '',
	'border-bottom-width': '',
	'border-left-width': '',
	'background-color': '#FFFFFF',
};

/**
 * Collapses the per-side border controls into the border attributes mj-wrapper
 * understands. Shared with the Listing Wrapper, which is a wrapper too.
 */
export function resolveWrapperAttributes(
	params: Parameters<NonNullable<IBlock['render']>>[0],
): IWrapper['attributes'] {
	const data = params.data as IWrapper;
	const borderWidth = data.attributes['border-width'];
	const borderStyle = data.attributes['border-style'];
	const borderColor = resolvePreviewColor(
		data.attributes['border-color'],
		params.mode,
		params.dataSource,
	);
	const borderTopWidth = data.attributes['border-top-width'];
	const borderRightWidth = data.attributes['border-right-width'];
	const borderBottomWidth = data.attributes['border-bottom-width'];
	const borderLeftWidth = data.attributes['border-left-width'];

	const hasSideBorderWidths = [
		borderTopWidth,
		borderRightWidth,
		borderBottomWidth,
		borderLeftWidth,
	].some(value => value !== undefined && value !== null && value !== '');

	if (hasSideBorderWidths) {
		const normalizedBorderStyle = borderStyle || 'solid';
		const normalizedBorderColor = borderColor || '#000000';

		return {
			...data.attributes,
			border: 'none',
			'border-top': `${borderTopWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
			'border-right': `${borderRightWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
			'border-bottom': `${borderBottomWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
			'border-left': `${borderLeftWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
		} as IWrapper['attributes'];
	}

	if (borderWidth || borderStyle || borderColor) {
		return {
			...data.attributes,
			border: [borderWidth || '0px', borderStyle || 'solid', borderColor || '#000000'].join(
				' ',
			),
		};
	}

	return data.attributes;
}

export const Wrapper = createBlock<IWrapper>({
	get name() {
		return t('Wrapper');
	},
	type: BasicType.WRAPPER,
	create: payload => {
		const defaultData: IWrapper = {
			type: BasicType.WRAPPER,
			data: {
				value: {},
			},
			attributes: { ...WRAPPER_DEFAULT_ATTRIBUTES },
			children: [],
		};
		return merge(defaultData, payload);
	},
	validParentType: [BasicType.PAGE],
	render(params) {
		const { data } = params;
		const attributes = resolveWrapperAttributes(params);

		return (
			<BasicBlock
				params={{
					...params,
					data: {
						...data,
						attributes,
					},
				}}
				tag='mj-wrapper'
			/>
		);
	},
});
